/**
 * Queue API Module
 *
 * Typed wrappers for all queue/attention endpoints via KodeonApiClient.
 *
 * The platform has unified on /v1/attention/* (operations service via
 * the platform-api gateway). The legacy /v1/approvals/* endpoints have
 * been retired and are no longer exposed by the gateway. Approve / reject
 * / suppress go through POST /v1/attention/{id}/resolve. Detail rendering
 * uses the AttentionItem.details payload that's already returned by
 * /v1/attention (see `buildSyntheticDetail.ts`).
 */

import { api } from '@/lib/api'
import type {
  AttentionListResponse,
  AttentionCountResponse,
  AttentionHistoryResponse,
  AttentionAssignmentsResponse,
} from './types'

// ============================================
// Removed: legacy /v1/approvals/* endpoints
// ============================================
//
// The platform unified on /v1/attention. The following endpoints used to
// exist on an older agents-service approval API and have been removed:
//
//   GET    /v1/approvals/queue
//   GET    /v1/approvals/{id}
//   POST   /v1/approvals/{id}/claim
//   POST   /v1/approvals/{id}/release
//   POST   /v1/approvals/{id}/approve
//   POST   /v1/approvals/{id}/suppress
//   POST   /v1/approvals/{id}/reject
//
// They are not routed through the platform-api gateway and have no
// upstream service backing them. Approve / reject / suppress now go
// through POST /v1/attention/{id}/resolve. Detail rendering uses the
// AttentionItem.details payload already in memory (see
// `buildSyntheticDetail.ts`). Claim / release have been replaced by
// tag-based assignment.

// ============================================
// Attention Endpoints (platform gateway)
// ============================================

/**
 * Fetch all attention items from the unified attention endpoint.
 */
export async function getAttentionItems(): Promise<AttentionListResponse> {
  return api.get<AttentionListResponse>('/v1/attention')
}

/**
 * Fetch attention count and hash for efficient polling.
 */
export async function getAttentionCount(): Promise<AttentionCountResponse> {
  return api.get<AttentionCountResponse>('/v1/attention/count')
}

/**
 * Fetch attention history — resolved items from all attention types.
 * Results are ordered by resolved_at descending (most recently actioned first).
 * Supports cursor-based pagination via the `cursor` param.
 */
export async function getAttentionHistory(params?: {
  limit?: number
  resolution?: string
  /** Opaque cursor from a previous response's next_cursor field. */
  cursor?: string
}): Promise<AttentionHistoryResponse> {
  const queryParams: Record<string, string | number> = {}
  if (params?.limit) {
    queryParams.limit = params.limit
  }
  if (params?.resolution) {
    queryParams.resolution = params.resolution
  }
  if (params?.cursor) {
    queryParams.cursor = params.cursor
  }
  return api.get<AttentionHistoryResponse>('/v1/attention/history', queryParams)
}

/**
 * Assign an attention item to an operator.
 */
export async function assignAttentionItem(
  id: string,
  assignedTo: string,
  assignedBy: string,
): Promise<void> {
  return api.post(`/v1/attention/${id}/assign`, {
    assigned_to: assignedTo,
    assigned_by: assignedBy,
  })
}

/**
 * Remove assignment from an attention item (back to <all>).
 */
export async function unassignAttentionItem(id: string): Promise<void> {
  return api.delete(`/v1/attention/${id}/assign`)
}

/**
 * Get all current attention item assignments.
 */
export async function getAttentionAssignments(): Promise<AttentionAssignmentsResponse> {
  return api.get<AttentionAssignmentsResponse>('/v1/attention/assignments')
}

/**
 * Response shape from POST /v1/attention/{id}/resolve.
 *
 * - `status === "resolved"` — normal success path; the agent / workflow
 *   accepted the resolution and transitioned state.
 * - `status === "already_resolved"` — Task 3145f idempotent-success path;
 *   the agent's pending attention had already been cleared before this
 *   request landed (double-click, two operators racing, or the agent
 *   self-completing). The endpoint still returns 200, the queue still
 *   refreshes, but the UI surfaces a neutral info toast instead of the
 *   regular success toast.
 *
 * Workflow paths (approve / reject / close / reply) return an
 * AutomationExecution object instead of a `{status, message}` envelope,
 * so the `status` field is optional and `Record<string, unknown>` is the
 * permissive shape callers actually receive.
 */
export interface ResolveAttentionResponse {
  status?: string
  message?: string
  [key: string]: unknown
}

/**
 * Map the frontend legacy `resolution` verb onto the canonical `action`
 * (4065a §4.2: accept|reject|suppress).
 *
 * The legacy verb space is wider (approve|reject|suppress|suppressed|reply|
 * close) than the canonical action space. The only verb that renames is
 * approve→accept; reject and suppress map 1:1 (suppressed normalises to
 * suppress). `reply`/`close` have no canonical equivalent — they are used
 * only by the legacy workflow/text-review dispatch paths that run under
 * flag-OFF, so they are passed through unchanged (the canonical handler
 * would reject them, but it is never the active handler for those paths).
 */
export function mapResolutionToCanonicalAction(resolution: string): string {
  switch (resolution) {
    case 'approve':
      return 'accept'
    case 'suppressed':
      return 'suppress'
    default:
      // reject, suppress pass through; reply/close are legacy-only.
      return resolution
  }
}

/**
 * Resolve an attention item. Routes to the correct backend based on
 * source and attention_type.
 */
export async function resolveAttentionItem(params: {
  id: string
  source: string
  attentionType: string
  resolution: string
  executionId?: string | null
  agentId?: string | null
  sessionId?: string | null
  reviewerId: string
  feedback?: string | null
  /** Verbatim copy of the item's details map at resolve time. Used by the backend
   *  to populate the history card with what the agent proposed (pending_message,
   *  proposed_resolution, args, etc.) for agent-sourced items that don't have a
   *  live execution row to pull this from. */
  detailsSnapshot?: Record<string, unknown> | null
}): Promise<ResolveAttentionResponse> {
  // Frontend validation: workflow items require execution_id
  if (params.source === 'workflow' && !params.executionId) {
    throw new Error(
      `Cannot resolve workflow item ${params.id}: execution_id is required but missing. ` +
      `This item may be malformed. Please refresh the queue and try again.`
    )
  }

  // Frontend validation: agent items require either agent_id or session_id
  if (params.source === 'agent' && !params.agentId && !params.sessionId) {
    throw new Error(
      `Cannot resolve agent item ${params.id}: either agent_id or session_id is required but both are missing. ` +
      `This item may be malformed. Please refresh the queue and try again.`
    )
  }

  // Build the request body, omitting null/undefined optional fields.
  // The backend validates required fields, so we omit them entirely if null
  // rather than sending explicit null values (which decode to "" in Go).
  //
  // ── Dual-shape resolve payload (Task 4068a / 4065a §4.2) ──────────────────
  // The operations service flag-splits POST /v1/attention/{id}/resolve on
  // ATTENTION_ITEM_STORE_ENABLED:
  //   - flag OFF (legacy AttentionHandler): reads `resolution` + `reviewer_id`.
  //   - flag ON  (canonical AttentionItemHandler): reads `action` + `operator_id`.
  // The two handlers read *disjoint* field sets, and Go's json.Decode silently
  // ignores unknown fields (neither uses DisallowUnknownFields). So we send
  // BOTH shapes in one body: the active handler reads its fields and ignores
  // the other set. This works under EITHER flag state, which is required
  // because 4068a ships before the backend cutover (4068b) flips the flag.
  //
  // `action` is the canonical verb (accept|reject|suppress) per 4065a §4.2;
  // `resolution` keeps the legacy verb (approve|reject|suppress|reply|close).
  // The only divergence is approve→accept; reject/suppress map 1:1.
  const body: Record<string, unknown> = {
    source: params.source,
    attention_type: params.attentionType,
    // Legacy fields (flag OFF).
    resolution: params.resolution,
    reviewer_id: params.reviewerId,
    // Canonical fields (flag ON). Map the legacy verb onto the canonical action.
    action: mapResolutionToCanonicalAction(params.resolution),
    operator_id: params.reviewerId,
  }

  // Only include optional fields if they have non-null values
  if (params.executionId) body.execution_id = params.executionId
  if (params.agentId) body.agent_id = params.agentId
  if (params.sessionId) body.session_id = params.sessionId
  if (params.feedback) body.feedback = params.feedback
  if (params.detailsSnapshot) body.details_snapshot = params.detailsSnapshot

  return api.post<ResolveAttentionResponse>(`/v1/attention/${params.id}/resolve`, body)
}

/**
 * Shape of the AutomationExecution object returned by
 * POST /v1/executions/{id}/summarise. Only the fields the inspector
 * renders are typed explicitly; everything else is preserved as an opaque
 * record so we don't break when the backend adds non-essential fields.
 */
export interface ExecutionSummaryResponse {
  id: string
  status: string
  execution_summary: Record<string, unknown> | null
  [key: string]: unknown
}

/**
 * Trigger on-demand summarisation of an execution. The previous automatic
 * lifecycle trigger was removed because it double-fired on every state
 * transition; operators now request a summary explicitly from the
 * Attention Queue's "Generate summary" button.
 *
 * Backend returns:
 * - 200 with the updated execution (including the new execution_summary).
 * - 400 if the execution is not in a terminal status (e.g. still running).
 * - 404 if the execution does not exist.
 * - 5xx if the LLM call or persistence fails.
 *
 * Callers are expected to surface 400/404/5xx via their existing error
 * toast machinery — the API client throws on any non-2xx.
 */
export async function generateExecutionSummary(
  executionId: string,
): Promise<ExecutionSummaryResponse> {
  return api.post<ExecutionSummaryResponse>(
    `/v1/executions/${executionId}/summarise`,
    {},
  )
}
